import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Card,
    CardContent,
    CardActionArea,
    Grid,
    Chip,
    CircularProgress,
    Breadcrumbs,
    Link,
    alpha,
    useTheme,
    IconButton,
    Avatar
} from '@mui/material';
import {
    Add as AddIcon,
    NavigateNext as NavigateNextIcon,
    Quiz as QuizIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CheckCircle as PublishedIcon,
    RadioButtonUnchecked as UnpublishedIcon
} from '@mui/icons-material';
import AdminLayout from 'pages/admin/components/AdminLayout';
import { getAllQuizzes } from 'services/quizService';
import { QuizListItem } from 'types/quiz';

const QuizStudioPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [quizzes, setQuizzes] = useState<QuizListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedQuiz, setSelectedQuiz] = useState<QuizListItem | null>(null);

    useEffect(() => {
        fetchQuizzes();
    }, []);

    useEffect(() => {
        if (id && quizzes.length > 0) {
            const quiz = quizzes.find(q => q.id === parseInt(id));
            setSelectedQuiz(quiz || null);
        } else {
            setSelectedQuiz(null);
        }
    }, [id, quizzes]);

    const fetchQuizzes = async () => {
        setLoading(true);
        try {
            const response = await getAllQuizzes(1, 100, true);
            setQuizzes(response.quizzes);
        } catch (error) {
            console.error('Error fetching quizzes:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateNew = () => {
        navigate('/admin/quizzes/new');
    };

    // Grid view when no quiz selected
    if (!id) {
        return (
            <AdminLayout>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                            <Typography variant="h4" fontWeight="bold" gutterBottom>
                                Quizzes
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                Create and manage interactive quizzes for students
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreateNew}
                            disabled={loading}
                        >
                            New Quiz
                        </Button>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {quizzes.map((quiz) => (
                                <Grid item xs={12} sm={6} md={4} key={quiz.id}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            transition: 'all 0.2s',
                                            background: quiz.isPublished
                                                ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.01)} 100%)`
                                                : 'background.paper',
                                            borderColor: quiz.isPublished
                                                ? alpha(theme.palette.success.main, 0.2)
                                                : 'divider',
                                            '&:hover': {
                                                boxShadow: 4,
                                                transform: 'translateY(-4px)',
                                                borderColor: quiz.isPublished
                                                    ? theme.palette.success.main
                                                    : theme.palette.primary.main
                                            }
                                        }}
                                    >
                                        <CardActionArea onClick={() => navigate(`/admin/quizzes/${quiz.id}/edit`)}>
                                            <CardContent>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 48,
                                                            height: 48,
                                                            bgcolor: quiz.isPublished
                                                                ? alpha(theme.palette.success.main, 0.1)
                                                                : alpha(theme.palette.grey[500], 0.1),
                                                            color: quiz.isPublished
                                                                ? theme.palette.success.main
                                                                : theme.palette.grey[500],
                                                            mr: 2
                                                        }}
                                                    >
                                                        <QuizIcon />
                                                    </Avatar>
                                                    <Box sx={{ flexGrow: 1 }}>
                                                        <Typography variant="h6" fontWeight="bold" gutterBottom>
                                                            {quiz.title}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                            <Chip
                                                                icon={quiz.isPublished ? <PublishedIcon fontSize="small" /> : <UnpublishedIcon fontSize="small" />}
                                                                label={quiz.isPublished ? 'Published' : 'Draft'}
                                                                size="small"
                                                                color={quiz.isPublished ? 'success' : 'default'}
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.7rem' }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    {quiz.description || 'No description'}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        label={`${quiz.questionCount || 0} Questions`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </AdminLayout>
        );
    }

    // Detail view (redirect to builder for now)
    return (
        <AdminLayout>
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/admin/quizzes');
                        }}
                        sx={{ cursor: 'pointer' }}
                    >
                        All Quizzes
                    </Link>
                    {selectedQuiz && (
                        <Typography color="text.primary" fontWeight="medium">
                            {selectedQuiz.title}
                        </Typography>
                    )}
                </Breadcrumbs>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : selectedQuiz ? (
                <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                    <Card variant="outlined" sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Avatar
                                sx={{
                                    width: 80,
                                    height: 80,
                                    bgcolor: selectedQuiz.isPublished
                                        ? alpha(theme.palette.success.main, 0.1)
                                        : alpha(theme.palette.grey[500], 0.1),
                                    color: selectedQuiz.isPublished
                                        ? theme.palette.success.main
                                        : theme.palette.grey[500],
                                    mr: 3
                                }}
                            >
                                <QuizIcon sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h4" fontWeight="bold" gutterBottom>
                                    {selectedQuiz.title}
                                </Typography>
                                <Chip
                                    icon={selectedQuiz.isPublished ? <PublishedIcon fontSize="small" /> : <UnpublishedIcon fontSize="small" />}
                                    label={selectedQuiz.isPublished ? 'Published' : 'Draft'}
                                    color={selectedQuiz.isPublished ? 'success' : 'default'}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton color="primary" onClick={() => navigate(`/admin/quizzes/${selectedQuiz.id}/edit`)}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <Typography variant="body1" color="text.secondary" paragraph>
                            {selectedQuiz.description || 'No description provided'}
                        </Typography>

                        <Grid container spacing={2} sx={{ mt: 2 }}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Questions
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {selectedQuiz.questionCount || 0}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Created
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {new Date(selectedQuiz.createdAt).toLocaleDateString()}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                            <Button
                                variant="contained"
                                startIcon={<EditIcon />}
                                onClick={() => navigate(`/admin/quizzes/${selectedQuiz.id}/edit`)}
                                fullWidth
                            >
                                Open Quiz Builder
                            </Button>
                        </Box>
                    </Card>
                </Box>
            ) : (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                    <Typography color="error">Quiz not found</Typography>
                </Box>
            )}
        </AdminLayout>
    );
};

export default QuizStudioPage;
